import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  type ChatSearchResponse,
  type ClickAction,
  type ClientVoice,
  type GooglePlace,
  type ReviewSummaryResponse,
  type ShownAstrologer,
  telUrl,
  whatsappUrl,
} from '@astrologermatch/shared';
import { Avatar, Stars } from './common.js';
import { SITE_NAME } from './Layout.js';
import { api, beacon } from '../lib/api.js';
import { useSettings } from '../lib/state.js';

// Ported from prototype/src/pages/Home.jsx (Results, AstroCard, ReviewSummary, GooglePanel,
// ClientVoices). Card clicks — "View profile & reviews", Call, WhatsApp, Website — are sent with
// the card's signed click token as billing facts: a click on an Ad card is what the astrologer
// pays for (pay-per-click, ADR-004). Nothing about the chat is sent.

function recordClick(a: ShownAstrologer, action: ClickAction) {
  beacon('/api/v1/clicks', { token: a.clickToken, action });
}

// Left click, Ctrl/Cmd-click and middle click (open in a new tab) all count; the server charges a
// card at most once, however many times it is clicked.
function trackClick(a: ShownAstrologer, action: ClickAction) {
  return {
    onClick: (e: MouseEvent) => {
      // The second click of a double-click would open the page twice (two history entries, so
      // Back seems to do nothing — LANE-D-5).
      if (e.detail > 1) {
        e.preventDefault();
        return;
      }
      recordClick(a, action);
    },
    onAuxClick: (e: MouseEvent) => {
      if (e.button === 1) recordClick(a, action);
    },
  };
}

const profilePath = (slug: string) => `/astrologers/${slug}`;

function ReviewSummary({ a }: { a: ShownAstrologer }) {
  const settings = useSettings();
  const [state, setState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [data, setData] = useState<ReviewSummaryResponse | null>(null);
  if (!settings.reviewSummaryButtonEnabled || !a.hasGoogleBusiness) return null;

  if (state === 'idle') {
    return (
      <button
        type="button"
        className="linkish small rs-btn"
        aria-label={`See review summary for ${a.name}`}
        onClick={() => {
          setState('loading');
          api<ReviewSummaryResponse>(
            'GET',
            `/api/v1/astrologers/${encodeURIComponent(a.slug)}/review-summary`,
          )
            .then(setData)
            .catch(() => {
              setData({ available: false });
            })
            .finally(() => {
              setState('done');
            });
        }}
      >
        See review summary
      </button>
    );
  }
  if (state === 'loading') {
    return (
      <div className="small muted rs-btn" role="status">
        <span className="dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>{' '}
        Loading review summary…
      </div>
    );
  }
  if (!data?.available) {
    return (
      <div className="small muted rs-btn">No review summary is available for this listing yet.</div>
    );
  }
  return (
    <div className="rs">
      <div className="rs-head">
        <b>Review summary</b>
        <span className="gm-attr">Google Maps</span>
      </div>
      <p>{data.summary}</p>
      <div className="rs-links tiny">
        {data.mapsReviewsUrl && (
          <a href={data.mapsReviewsUrl} target="_blank" rel="noreferrer nofollow">
            See reviews on Google Maps
          </a>
        )}
        {data.reportUrl && (
          <a href={data.reportUrl} target="_blank" rel="noreferrer nofollow">
            Report summary
          </a>
        )}
      </div>
    </div>
  );
}

function AstroCard({
  a,
  sponsored = false,
  compact = false,
}: {
  a: ShownAstrologer;
  sponsored?: boolean;
  compact?: boolean;
}) {
  const wa = whatsappUrl(a.whatsapp ?? a.phone);
  const tel = telUrl(a.phone);
  return (
    <article className={`match-card${compact ? ' compact' : ''}`}>
      {sponsored && (
        <div className="match-top">
          <span className="rank-badge">#{a.rank}</span>
          {a.isAd && (
            <span className="ad-label" title="Paid placement">
              Ad
            </span>
          )}
        </div>
      )}
      <div className="match-id">
        <Avatar name={a.name} photoUrl={a.photoUrl} />
        <div>
          <h4>{a.name}</h4>
          {a.practiceName && <div className="muted small">{a.practiceName}</div>}
          {a.rating !== null ? (
            <div className="small">
              <Stars value={a.rating} /> <b>{a.rating}</b>{' '}
              <Link
                to={profilePath(a.slug)}
                className="muted"
                aria-label={`${String(a.reviewCount)} reviews of ${a.name}`}
              >
                ({a.reviewCount} reviews)
              </Link>
            </div>
          ) : (
            <div className="small muted">New on {SITE_NAME}</div>
          )}
          <div className="verified tiny">✓ Verified business · website checked</div>
        </div>
      </div>
      <div className="chips">
        {a.specialities.slice(0, 3).map((s) => (
          <span key={s} className="chip">
            {s}
          </span>
        ))}
      </div>
      <div className="why">
        <div className="why-label">Why this astrologer matches your problem</div>
        <p>{a.summary}</p>
      </div>
      <div className="card-links">
        <Link
          to={profilePath(a.slug)}
          className="linkish small"
          aria-label={`View profile & reviews of ${a.name}`}
          {...trackClick(a, 'PROFILE')}
        >
          View profile &amp; reviews →
        </Link>
        <ReviewSummary a={a} />
      </div>
      <div className="meta small muted">
        {a.city}, {a.country}
        {a.experienceYears ? ` · ${String(a.experienceYears)} yrs` : ''} ·{' '}
        {a.consultsOnline ? 'Online & in person' : 'In person'}
      </div>
      <div className="contact-row">
        {tel && (
          <a
            className="btn btn-gold btn-sm"
            href={tel}
            aria-label={`Call ${a.name}`}
            {...trackClick(a, 'CALL')}
          >
            Call
          </a>
        )}
        {wa && (
          <a
            className="btn btn-outline btn-sm"
            href={wa}
            target="_blank"
            rel="noreferrer"
            aria-label={`WhatsApp ${a.name}`}
            {...trackClick(a, 'WHATSAPP')}
          >
            WhatsApp
          </a>
        )}
        {a.websiteUrl && (
          <a
            className="btn btn-outline btn-sm"
            href={a.websiteUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`Website of ${a.name}`}
            {...trackClick(a, 'WEBSITE')}
          >
            Website
          </a>
        )}
      </div>
    </article>
  );
}

function GooglePanel({ places, mapsSearchUrl }: { places: GooglePlace[]; mapsSearchUrl: string }) {
  return (
    <aside className="gpanel" aria-label="Astrologers on Google Maps">
      <div className="gpanel-head">
        <b>On Google Maps</b>
        <span className="tiny muted">Top organic listings · no ads</span>
      </div>
      <ol className="gpanel-list">
        {places.map((g) => (
          <li key={g.id}>
            {g.googleMapsUri ? (
              <a
                href={g.googleMapsUri}
                target="_blank"
                rel="noreferrer nofollow"
                className="gp-name"
              >
                {g.displayName}
              </a>
            ) : (
              <span className="gp-name">{g.displayName}</span>
            )}
            {g.rating !== undefined && (
              <div className="tiny">
                <Stars value={g.rating} /> <b>{g.rating}</b>{' '}
                {g.userRatingCount !== undefined && (
                  <span className="muted">({g.userRatingCount})</span>
                )}
              </div>
            )}
            <div className="tiny muted">
              {g.formattedAddress}
              {g.openNow !== undefined && (
                <>
                  {' · '}
                  <span className={g.openNow ? 'ok' : ''}>{g.openNow ? 'Open now' : 'Closed'}</span>
                </>
              )}
            </div>
          </li>
        ))}
      </ol>
      <div className="gpanel-foot">
        <span className="gm-attr">Google Maps</span>
        <a className="tiny" href={mapsSearchUrl} target="_blank" rel="noreferrer nofollow">
          See more on Google Maps
        </a>
      </div>
    </aside>
  );
}

function clipVoice(text: string): string {
  return text.length > 110 ? `${text.slice(0, text.lastIndexOf(' ', 108))}…` : text;
}

function ClientVoices({
  voices,
  verifiedCount,
  reviewCount,
}: {
  voices: ClientVoice[];
  verifiedCount: number;
  reviewCount: number;
}) {
  return (
    <aside className="gpanel voices" aria-label="What clients are saying">
      <div className="gpanel-head">
        <b>What clients are saying</b>
        <span className="tiny muted">Recent reviews from real clients</span>
      </div>
      <ul className="voice-list">
        {voices.map((r) => (
          <li key={r.id}>
            <Stars value={r.rating} />
            <p>“{clipVoice(r.text)}”</p>
            <div className="tiny muted">
              — {r.reviewerName}, about{' '}
              <Link to={profilePath(r.astrologerSlug)}>{r.astrologerName}</Link>
            </div>
          </li>
        ))}
      </ul>
      <div className="trust-box">
        <div>
          <b>{verifiedCount}</b>
          <span>verified astrologers</span>
        </div>
        <div>
          <b>{reviewCount}</b>
          <span>client reviews</span>
        </div>
      </div>
    </aside>
  );
}

export function Results({ data }: { data: ChatSearchResponse }) {
  const none = data.topMatches.length === 0 && data.highlyRated.length === 0;
  const panel = data.panel;
  const hasPanel = panel.mode !== 'none';
  return (
    <div className="results">
      <div className="detected small">
        <span className="muted">Understood as:</span>
        {data.intentLabels.length ? (
          data.intentLabels.map((l) => (
            <span key={l} className="chip chip-soft">
              {l}
            </span>
          ))
        ) : (
          <span className="chip chip-soft">General reading</span>
        )}
        <span className="muted">·</span>
        <span className="chip chip-soft">{data.locationLabel ?? 'Worldwide'}</span>
      </div>

      <div className={`results-layout${hasPanel ? ' with-panel' : ''}`}>
        <div className="results-main">
          {none && (
            <div className="empty-note">
              <b>No verified astrologer matches this yet.</b> Try describing your concern
              differently, or choose “Online is fine” — many astrologers consult by phone or video
              from anywhere.
            </div>
          )}

          {data.topMatches.length > 0 && (
            <>
              <h3 className="res-h">Top matches for your problem</h3>
              <div className="match-grid">
                {data.topMatches.map((a) => (
                  <AstroCard key={a.id} a={a} sponsored />
                ))}
              </div>
            </>
          )}

          {data.highlyRated.length > 0 && (
            <>
              <h3 className="res-h">Highly rated by clients</h3>
              <div className="match-grid">
                {data.highlyRated.map((a) => (
                  <AstroCard key={a.id} a={a} compact />
                ))}
              </div>
            </>
          )}
          {!none && (
            <p className="tiny muted">
              Verified astrologers only. Top matches are ads; the rest are ranked by reviews and
              ratings from real clients on {SITE_NAME}. Contact astrologers directly — we don&apos;t
              take any payment from you.
            </p>
          )}
        </div>
        {panel.mode === 'google' && (
          <GooglePanel places={panel.places} mapsSearchUrl={panel.mapsSearchUrl} />
        )}
        {panel.mode === 'reviews' && (
          <ClientVoices
            voices={panel.voices}
            verifiedCount={panel.verifiedCount}
            reviewCount={panel.reviewCount}
          />
        )}
      </div>
    </div>
  );
}
